import React from 'react';
import styled from 'styled-components';

import { ApiFill } from '#/api/types';
import RouterLink from '#/components/basic/Link/RouterLink';
import StopEventPropagationDiv from '#/components/basic/panels/StopEventPropagationDiv';
import BasicTableStackable from '#/components/basic/table/BasicTableStackable';
import {
  EXPAND_COLUMN_WIDTH,
  ExpandColumnIcon,
} from '#/components/basic/table/components/ExpandColumnIcon';
import TextColored from '#/components/basic/text/TextColored';
import { useSelectorActiveChain } from '#/components/hooks';
import PnLShareShortcut from '#/components/positions/pnLShare/PnLShareShortcut';
import { TableCoinIcon } from '#/config/coinIcons/CoinIcon';
import { EXCHANGE_DECIMALS } from '#/config/constants';
import { formatDateTime } from '#/lib/date';
import {
  formatNumber,
  formatPrice,
  formatUsdWithSign,
} from '#/lib/display/formatting';
import { redirectTo } from '#/lib/redirect';
import { capitalizeFirstLetter } from '#/lib/string';
import { getDashboardUrl } from '#/reduxState/markets/listed/listedMarketsUtils';
import { screenSizes } from '#/reduxState/screen/screenUtils';
import { useSelector } from '#/reduxState/store';

import MyTradeClickableStatus from './MyTradeClickableStatus';

const Capitalized = styled.span`
  text-transform: capitalize;
`;

type Props = {
  trades: (ApiFill & {
    baseAssetSymbol: string;
    // quoteAssetSymbol: string;
  })[];
  appearAnimationEnabled?: boolean;
  loading: boolean;
  onSelectOrder?: (
    orderId: string,
    highlightFillId: string /* trade: ApiFill */
  ) => void;
  vaultManagerWallet?: string;
};

export default function MyTradesTable({
  trades,
  appearAnimationEnabled,
  loading,
  onSelectOrder,
  vaultManagerWallet,
}: Props) {
  const isMobile = useSelector(state => state.screen.isMobile);
  const isTablet = useSelector(state => state.screen.isTablet);
  const resolutionIndex = useSelector(state => state.screen.resolutionIndex);
  const isLargerMobile = resolutionIndex > screenSizes.micro && isMobile;

  const loggedIn = useSelector(state => state.currentUser.loggedIn);
  const userInitialized = useSelector(state => state.currentUser.initialized);
  const walletAddress = useSelector(state => state.wallet.walletAddress);
  const tickSizes = useSelector(state => state.markets.listedMarkets.tickSizes);
  const stepSizes = useSelector(state => state.markets.listedMarkets.stepSizes);
  const { activeChain } = useSelectorActiveChain();

  const walletAvailable = walletAddress || vaultManagerWallet;

  return (
    <BasicTableStackable
      notFixedLayout
      items={trades}
      firstColumnPaddingLeft="15px"
      loading={!!walletAvailable && loading}
      appearAnimationColor={
        // Display nice effect for realtime fills (from websocket),
        // but not when loading more pages
        appearAnimationEnabled
          ? trade => (trade.side === 'buy' ? 'green' : 'red')
          : undefined
      }
      stripes
      errorText={
        !walletAvailable
          ? 'Connect wallet to see your trades'
          : userInitialized && !loading
          ? loggedIn
            ? trades.length === 0
              ? 'No trade history.'
              : undefined
            : 'User not logged in'
          : '' // loading
      }
      onRowClick={
        typeof onSelectOrder === 'function'
          ? trade =>
              trade.orderId
                ? onSelectOrder(trade.orderId, trade.fillId)
                : undefined
          : undefined
      }
      getItemKey={item => item.fillId}
      columns={trade => {
        const tickSizeDecimals =
          tickSizes?.[trade?.market || '']?.visibleDecimals ??
          EXCHANGE_DECIMALS;
        const stepSizeDecimals =
          stepSizes?.[trade?.market || '']?.visibleDecimals ??
          EXCHANGE_DECIMALS;

        const sideColumn = trade ? (
          <TextColored color={trade.side === 'buy' ? 'green' : 'red'}>
            <Capitalized>{trade.side}</Capitalized>
          </TextColored>
        ) : null;
        /**
         * https://auroradao.atlassian.net/browse/PB-142
         * On the trade history page, the price shown for liquidations is the bankruptcy price,
         * i.e. the price at which the insurance fund acquired the position and the price at
         * which the account value is equal to zero. This is almost always a "worse" price than
         * the liquidation price and leads to confusion for the user.
         * In particular it's common that the price we show them for the liquidation was never reached by either the index price or trades on IDEX.
         */
        const price =
          trade?.type === 'liquidation'
            ? Number(trade.indexPrice) || trade?.price // Fallback if not available (Not yet backfilled)
            : trade?.price;
        const value =
          trade?.type === 'liquidation'
            ? Number(price) * Number(trade.quantity)
            : trade?.quoteQuantity;

        return [
          {
            name: 'Date',
            render: <>{formatDateTime(trade?.time)}</>,
          },
          {
            name: 'Market',
            stackWithPrevious: isMobile,
            fontFamily: 'standard',
            render: isMobile ? (
              <>
                <TableCoinIcon symbol={trade?.baseAssetSymbol} />
                {trade?.market}
              </>
            ) : (
              <StopEventPropagationDiv>
                <RouterLink
                  to={getDashboardUrl(activeChain, trade?.market)}
                  title="Open market"
                  color="secondary"
                >
                  <TableCoinIcon symbol={trade?.baseAssetSymbol} />
                  {trade?.market}
                </RouterLink>
              </StopEventPropagationDiv>
            ),
          },
          {
            name: 'Side',
            fontFamily: 'standard',
            render: isMobile ? false : sideColumn,
          },
          {
            name: 'Price',
            render:
              trade && price ? formatPrice(price, tickSizeDecimals) : null,
            stackWithPrevious: isTablet,
          },
          {
            name: 'Quantity',
            stackWithPrevious: isMobile,
            render: (
              <>
                {formatNumber(trade?.quantity, {
                  fixedDecimals: stepSizeDecimals,
                })}{' '}
                {trade?.baseAssetSymbol}
              </>
            ),
          },
          {
            name: 'Value',
            render:
              isMobile && !isLargerMobile ? false : formatUsdWithSign(value),
          },
          {
            name: 'Trade Fee',
            render: isMobile ? (
              false
            ) : (
              <>{trade?.fee ? formatUsdWithSign(trade.fee) : '-'}</>
            ),
          },
          {
            name: 'Type',
            fontFamily: 'standard',
            render: isMobile ? (
              false
            ) : (
              <>{capitalizeFirstLetter(trade?.type)}</>
            ),
          },
          {
            name: 'Liquidity',
            render: isMobile ? false : capitalizeFirstLetter(trade?.liquidity),
          },
          {
            name: 'Realized P&L',
            align: isMobile ? 'right' : 'left',
            render: trade => (
              <TextColored
                color={Number(trade.realizedPnL) < 0 ? 'red' : 'green'}
              >
                {formatUsdWithSign(trade.realizedPnL)}
                <PnLShareShortcut market={trade.market} fill={trade} />
              </TextColored>
            ),
          },
          {
            name: 'Side',
            stackWithPrevious: true,
            fontFamily: 'standard',
            render: isMobile ? sideColumn : false,
          },
          {
            name: 'Status',
            fontFamily: 'standard',
            // align: vaultManagerWallet ? 'right' : 'left',
            render: isMobile ? (
              false
            ) : trade ? (
              <MyTradeClickableStatus fill={trade} activeChain={activeChain} />
            ) : null,
          },
          {
            name: '',
            width: EXPAND_COLUMN_WIDTH,
            onValueClick:
              isMobile || !trade?.orderId
                ? undefined
                : () => {
                    if (!trade.orderId) {
                      return;
                    }
                    if (onSelectOrder) {
                      onSelectOrder(trade.orderId, trade.fillId);
                    } else {
                      // On Wallet sub-page we use redirect
                      redirectTo(
                        `/wallet/history/trades/${trade.orderId}/${trade?.fillId}`
                      );
                    }
                  },
            // Not available for Deleverage and Liquidation
            render:
              isMobile || vaultManagerWallet ? (
                false
              ) : trade?.orderId ? (
                <ExpandColumnIcon />
              ) : null,
          },
        ];
      }}
    />
  );
}
